import { MapRef } from '@components/pages/franchise/map'
import { SearchData } from '@components/pages/franchise/search'
import { IndexBlockCallDto } from '@services/api/block-calls'
import { CompanyParams } from '@services/api/companies'
import {
  Franchise,
  FranchiseDto,
  FranchiseIndexDto
} from '@services/api/franchise/types'
import { Location } from '@services/api/locations'
import { useRouter } from 'next/router'
import { createContext, useContext, useCallback, useRef, useState } from 'react'

const BRASIL = {
  LAT: -14.103939,
  LONG: -50.701436
}

interface FranchiseContextData {
  isMap?: boolean
  responseFranchisePagination: FranchiseIndexDto
  handleOnClickCompany?: (slug: string) => void
  handleClickBack?: () => void
  franchise?: Franchise
  loading?: boolean
  params?: CompanyParams
  update?: (page?: number, search?: SearchData) => void
  totalFranchises?: number
  franchises?: FranchiseDto[]
  mapRef?: any
  blockCallsVideo?: IndexBlockCallDto
  franchiseSelectedByMap?: FranchiseDto
  setFranchiseSelectedByMap?: (franchise: FranchiseDto) => void
  location?: Location
}

interface FranchiseProviderData extends FranchiseContextData {
  children: React.ReactNode
}

const FranchiseContext = createContext({} as FranchiseContextData)

export const FranchiseProvider: React.FC<FranchiseProviderData> = ({
  children,
  responseFranchisePagination,
  params,
  isMap,
  blockCallsVideo,
  franchise,
  location
}) => {
  const { content: franchises, totalFranchises: totalCompanies } =
    responseFranchisePagination || {
      content: undefined,
      totalCount: 0
    }

  const [loading] = useState(false)
  const [franchiseSelectedByMap, setFranchiseSelectedByMap] =
    useState<FranchiseDto>(undefined)

  const router = useRouter()
  const mapRef = useRef<MapRef>(null)

  const handleOnClickCompany = useCallback((slug: string) => {
    window.open(
      `/fornecedores/empresas-de-energia-solar/parana/${slug}`,
      '_blank'
    )
  }, [])

  const handleClickBack = useCallback(() => {
    setFranchiseSelectedByMap(undefined)
    mapRef.current?.setPos(BRASIL.LAT, BRASIL.LONG)
    mapRef.current?.setZoom(4)
  }, [])

  const update = useCallback((page?: number, search?: SearchData) => {
    const { location } = search

    let label = '/fornecedores/empresas-de-energia-solar/'

    if (location?.state) {
      label += [location.state.slug, location.slug].join('/')
    } else {
      label += location.slug
    }

    router.push(label)
  }, [])

  return (
    <FranchiseContext.Provider
      value={{
        handleOnClickCompany,
        handleClickBack,
        franchise,
        loading,
        responseFranchisePagination,
        params,
        update,
        totalFranchises: totalCompanies,
        franchises,
        mapRef,
        isMap,
        blockCallsVideo,
        franchiseSelectedByMap,
        setFranchiseSelectedByMap,
        location
      }}
    >
      {children}
    </FranchiseContext.Provider>
  )
}

export const useFranchise = (): FranchiseContextData => {
  return useContext(FranchiseContext)
}
